import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Dexie from 'dexie';
import API_CONFIG from '../components/API_CONFIG/API_CONFIG';

// Import des composants
import CunstomProfilEntreprise from '../components/maketApp/layout/CunstomProfilEntreprise';


// --- Configuration Dexie (base de données IndexedDB) ---
const db = new Dexie('UserCacheDB');
db.version(1).stores({
  userData: 'userId, expiry' // userId est la clé primaire
});

// Fonctions utilitaires pour le cache
async function getCachedUserData(userId) {
  try {
    const record = await db.userData.get(userId);
    if (record && record.expiry > Date.now()) {
      return record.data;
    }
    return null;
  } catch (error) {
    console.warn('Erreur lecture cache Dexie:', error);
    return null;
  }
}

async function setCachedUserData(userId, data) {
  try {
    await db.userData.put({
      userId: userId,
      data: data,
      expiry: Date.now() + 24 * 60 * 60 * 1000 // 24 heures
    });
  } catch (error) {
    console.warn('Erreur écriture cache Dexie:', error);
  }
}

// --- Composant Home ---
const Entreprise = () => {
  const navigate = useNavigate();

  // 1. Récupération de l'userId depuis localStorage (inchangé)
  const [userId] = useState(() => localStorage.getItem('userId'));

  // 2. État utilisateur (initialisé à null, sera chargé depuis Dexie)
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Référence pour éviter les mises à jour après démontage
  const isMounted = useRef(true);

  // Fonction de déconnexion
  const handleLogout = () => {
    console.warn('Statut utilisateur désactivé ou session invalide.');
    localStorage.clear();
    navigate('/');
  };

  // 3. Effet principal : lecture du cache + requête API si nécessaire
  useEffect(() => {
    isMounted.current = true;
    const controller = new AbortController();

    const fetchData = async () => {
      if (!userId) {
        if (isMounted.current) {
          setLoading(false);
        }
        return;
      }

      // --- Étape 1 : Lire le cache Dexie ---
      const cached = await getCachedUserData(userId);
      if (isMounted.current && cached) {
        // Cache valide : on l'utilise immédiatement
        setUserData(cached);
        setLoading(false);
        // Pas besoin de requête API, on s'arrête là
        return;
      }

      // --- Étape 2 : Pas de cache valide → appel API ---
      try {
        const response = await axios.get(`${API_CONFIG.apiUrl}/getUserInfo.php`, {
          params: { userId },
          signal: controller.signal
        });

        const data = response.data;

        if (isMounted.current) {
          // Vérification du statut (0 = désactivé)
          if (data && data.status === 0) {
            handleLogout();
            return;
          }

          // Mise à jour de l'état et du cache
          setUserData(data);
          await setCachedUserData(userId, data);
          setLoading(false);
        }
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log('Requête annulée');
        } else {
          console.error('Erreur API:', error);
          if (isMounted.current) {
            // En cas d'échec, on peut afficher un message ou laisser le spinner
            setLoading(false);
          }
        }
      }
    };

    fetchData();

    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, [userId, navigate]);

  // 4. Mappage des composants en fonction du rôle (inchangé)
  const ComponentToRender = useMemo(() => {
    if (!userData) return null;

    const roleComponents = {
      'SUPER ADMIN': CunstomProfilEntreprise,
      'DSI': CunstomProfilEntreprise,
      
    };

    const SpecificComponent = roleComponents[userData.fonction];
    return SpecificComponent ? (
      <SpecificComponent userId={userId} fonction={userData.fonction} />
    ) : (
      <div className="text-center mt-5 text-white">Rôle non reconnu ou accès non autorisé.</div>
    );
  }, [userData, userId]);

  // 5. Affichage du spinner pendant le chargement
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100 bg-dark text-white">
        <div className="spinner-border text-warning" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="adminx-container">
      {ComponentToRender}
    </div>
  );
};

export default Entreprise;